/**
 * V1 Scoring Engine
 *
 * Calculates a single canonical strength score from the 5 foundational lifts.
 * - Bodyweight-normalized
 * - Per-lift contribution capped at 25%
 * - Freshness-weighted (decay applied)
 * - Provenance-weighted (verified > attested > unverified)
 *
 * Constitutional Sentence: DUEL measures strength as generalized,
 * bodyweight-normalized performance across foundational lifts.
 */

import {
  V1_LIFT_POOL,
  V1_LIFT_LABELS,
  type V1Lift,
  normalizeByBodyweight,
} from '@/lib/lifts/canonical';
import { calculateLiftFreshness, type FreshnessStatus } from '@/lib/baselineGate';

/** Provenance tiers and their score multipliers */
export type ProvenanceTier = 'unverified' | 'attested' | 'verified';

const PROVENANCE_MULTIPLIER: Record<ProvenanceTier, number> = {
  unverified: 0.7,
  attested: 0.85,
  verified: 1.0,
};

export interface LiftInput {
  lift: V1Lift;
  /** Raw score (e1RM for standard lifts, holdTimeSec for dead hang) */
  rawScore: number;
  /** Date the lift was performed */
  performedAt: string;
  /** Provenance tier */
  provenance: ProvenanceTier;
}

export interface ScoreBreakdown {
  /** Final composite score */
  totalScore: number;
  /** Per-lift breakdown */
  lifts: {
    lift: V1Lift;
    label: string;
    rawScore: number;
    bwNormalized: number;
    freshnessMultiplier: number;
    provenanceMultiplier: number;
    adjustedScore: number;
    /** Contribution after capping */
    cappedScore: number;
    /** Percentage of total */
    contributionPct: number;
    freshnessStatus: FreshnessStatus;
    provenance: ProvenanceTier;
  }[];
  /** Bodyweight used for normalization */
  bodyweight: number;
  /** Whether the per-lift cap was applied to any lift */
  capApplied: boolean;
}

/**
 * Calculate V1 composite strength score with full breakdown.
 */
export function calculateV1Score(
  liftInputs: LiftInput[],
  bodyweight: number,
  now: Date = new Date()
): ScoreBreakdown {
  const lifts = V1_LIFT_POOL.map((liftKey) => {
    const input = liftInputs.find((l) => l.lift === liftKey);
    const label = V1_LIFT_LABELS[liftKey];

    if (!input) {
      return {
        lift: liftKey,
        label,
        rawScore: 0,
        bwNormalized: 0,
        freshnessMultiplier: 0,
        provenanceMultiplier: 0,
        adjustedScore: 0,
        cappedScore: 0,
        contributionPct: 0,
        freshnessStatus: 'missing' as FreshnessStatus,
        provenance: 'unverified' as ProvenanceTier,
      };
    }

    const freshness = calculateLiftFreshness(liftKey, input.performedAt, now);
    const bwNormalized = bodyweight > 0 ? normalizeByBodyweight(input.rawScore, bodyweight) : input.rawScore;
    const provenanceMultiplier = PROVENANCE_MULTIPLIER[input.provenance];
    const adjustedScore = bwNormalized * freshness.contributionMultiplier * provenanceMultiplier;

    return {
      lift: liftKey,
      label,
      rawScore: input.rawScore,
      bwNormalized: Math.round(bwNormalized * 100) / 100,
      freshnessMultiplier: freshness.contributionMultiplier,
      provenanceMultiplier,
      adjustedScore: Math.round(adjustedScore * 100) / 100,
      cappedScore: 0, // will be set after capping
      contributionPct: 0, // will be set after total
      freshnessStatus: freshness.status,
      provenance: input.provenance,
    };
  });

  // Sum all adjusted scores
  const rawTotal = lifts.reduce((sum, l) => sum + l.adjustedScore, 0);

  if (rawTotal === 0) {
    return { totalScore: 0, lifts, bodyweight, capApplied: false };
  }

  // Apply per-lift cap: no lift contributes more than 25% of the pre-cap total
  const maxContribution = rawTotal * 0.25;
  let capApplied = false;

  for (const lift of lifts) {
    if (lift.adjustedScore > maxContribution) {
      lift.cappedScore = maxContribution;
      capApplied = true;
    } else {
      lift.cappedScore = lift.adjustedScore;
    }
  }

  const totalScore = lifts.reduce((sum, l) => sum + l.cappedScore, 0);

  // Calculate contribution percentages
  for (const lift of lifts) {
    lift.contributionPct = totalScore > 0 ? Math.round((lift.cappedScore / totalScore) * 100) : 0;
  }

  return {
    totalScore: Math.round(totalScore * 100),
    lifts,
    bodyweight,
    capApplied,
  };
}
